import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Icon from '../../common/icons';
import AssessmentIcon from '../../common/AssessmentIcon';
import api from '../../../utils/api';
import { detectPlatform } from '../../../pwa/platform';
import { waitingCount } from './signOutAnswers';
import './testShell.css';

const tx = (lang, en, fil) => (lang === 'fil' ? fil : en);
/** "phone", "iPad" or "computer", like the waiting note in the chat */
const deviceName = () => {
  try {
    const platform = detectPlatform();
    if (platform.ipad) return 'iPad';
    return platform.mobile ? 'phone' : 'computer';
  } catch {
    return 'device';
  }
};
const STAR_WORDS = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Very good', 5: 'Excellent' };

/**
 * "How were today's tests?": one star row per test finished in this chat session. Opens when
 * the student leaves the chat (never on top of a result, never after worrying answers).
 * tests: [{ key, title, category }]; a test already rated today is left out.
 */
export const SessionRatingModal = ({ show, tests = [], language = 'en', onDone }) => {
  const lang = language === 'fil' ? 'fil' : 'en';
  const [rows, setRows] = useState([]);
  const [ratings, setRatings] = useState({});
  const [hover, setHover] = useState({});
  const [comment, setComment] = useState('');
  const [course, setCourse] = useState('');
  const [sending, setSending] = useState(false);
  const [checked, setChecked] = useState(false);

  // Leave out tests the student already rated today (the server takes one rating a day per test)
  useEffect(() => {
    if (!show) {
      setChecked(false);
      return;
    }
    let cancelled = false;
    setRatings({});
    setHover({});
    setComment('');
    setCourse('');
    (async () => {
      const open = [];
      for (const test of tests) {
        try {
          const response = await api.get('/api/check-feedback/', { params: { category: test.category } });
          if (!response?.data?.exists) open.push(test);
        } catch {
          open.push(test);
        }
      }
      if (cancelled) return;
      setRows(open);
      setChecked(true);
      if (open.length === 0 && onDone) onDone({ skipped: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [show]);

  const rated = rows.filter((row) => ratings[row.key] > 0);
  const hasRiasec = rows.some((row) => row.key === 'riasec');

  const send = async (event) => {
    event?.preventDefault?.();
    if (!rated.length || sending) return;
    setSending(true);
    try {
      for (const row of rated) {
        await api.post('/api/submit-feedback/', {
          category: row.category,
          rating: ratings[row.key],
          feedback: comment.trim() || null,
          intended_course: row.key === 'riasec' ? course.trim() : '',
        });
      }
      toast.success(tx(lang, 'Thanks for telling us!', 'Salamat sa pagsabi sa amin!'));
      if (onDone) onDone({ skipped: false });
    } catch (error) {
      toast.error(error?.response?.data?.error || tx(lang, "Couldn't send that. Try again.", 'Hindi naipadala. Subukan ulit.'));
    } finally {
      setSending(false);
    }
  };

  const skip = () => {
    if (onDone) onDone({ skipped: true });
  };

  const title = rows.length === 1
    ? tx(lang, `How was ${rows[0].phrase || rows[0].title}?`, `Kumusta ang ${rows[0].title}?`)
    : tx(lang, "How were today's tests?", 'Kumusta ang mga test ngayon?');

  return (
    <Modal show={show && checked && rows.length > 0} onHide={skip} centered className="ts-rate-modal">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <form onSubmit={send}>
        <Modal.Body>
          <div className="am-form">
            <div className="ts-rate-rows">
              {rows.map((row) => {
                const value = hover[row.key] || ratings[row.key] || 0;
                return (
                  <div className="ts-rate-row" key={row.key}>
                    <span className="ts-rate-name">
                      <AssessmentIcon type={row.key} size={20} />
                      {row.title}
                    </span>
                    <div className="am-stars" role="radiogroup" aria-label={tx(lang, `Rate ${row.title}`, `I-rate ang ${row.title}`)}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          role="radio"
                          aria-checked={ratings[row.key] === star}
                          aria-label={`${star} – ${STAR_WORDS[star]}`}
                          className={`am-star${value >= star ? ' is-on' : ''}`}
                          onClick={() => setRatings((prev) => ({ ...prev, [row.key]: star }))}
                          onMouseEnter={() => setHover((prev) => ({ ...prev, [row.key]: star }))}
                          onMouseLeave={() => setHover((prev) => ({ ...prev, [row.key]: 0 }))}
                        >
                          <Icon name="star" size={26} />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {hasRiasec && (
              <label className="am-field">
                <span className="am-label">
                  {tx(lang, 'Did ANNA suggest the course you had in mind? If not, which one?', 'Nabanggit ba ni ANNA ang kursong gusto mo? Kung hindi, ano ito?')}{' '}
                  <span className="am-optional">{tx(lang, '(optional)', '(opsyonal)')}</span>
                </span>
                <span className="am-input">
                  <Icon name="graduation-cap" size={20} />
                  <input
                    type="text"
                    value={course}
                    onChange={(e) => setCourse(e.target.value)}
                    placeholder={tx(lang, 'Type a course', 'Mag-type ng kurso')}
                    maxLength={120}
                  />
                </span>
              </label>
            )}

            <label className="am-field">
              <span className="am-label">
                {tx(lang, 'Anything we could do better?', 'May gusto ka bang ipaayos?')}{' '}
                <span className="am-optional">{tx(lang, '(optional)', '(opsyonal)')}</span>
              </span>
              <span className="am-input">
                <textarea rows="2" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000} />
              </span>
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer className="am-foot-split">
          <button type="button" className="am-btn is-soft" onClick={skip}>
            {tx(lang, 'Not now', 'Mamaya na')}
          </button>
          <button type="submit" className="am-btn is-primary" disabled={!rated.length || sending}>
            {sending ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : <Icon name="send" size={18} />}
            {tx(lang, 'Send', 'Ipadala')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

/** "Leave for now?" when the student closes the chat in the middle of a test. */
export const LeaveTestDialog = ({ show, question, testTitle, language = 'en', onStay, onLeave }) => {
  const lang = language === 'fil' ? 'fil' : 'en';
  // Answers still on this device, not yet at the school: say so instead of "saved" (F21)
  const waiting = show ? waitingCount() : 0;
  const device = deviceName();
  const pickUp = question
    ? tx(lang,
      `Next time you open Anna, you'll pick up the ${testTitle || 'test'} at question ${question}.`,
      `Sa susunod na buksan mo si Anna, itutuloy mo ang ${testTitle || 'test'} sa tanong ${question}.`)
    : tx(lang, 'You can pick up where you left off next time.', 'Puwede mong ituloy kung saan ka huminto sa susunod.');
  return (
    <Modal show={show} onHide={onStay} centered className="ts-leave-modal">
      <Modal.Header closeButton>
        <Modal.Title>{tx(lang, 'Leave for now?', 'Aalis ka muna?')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="am-intro am-intro-only">
          <span className="am-intro-icon">
            <Icon name={waiting ? 'clock' : 'check'} size={28} />
          </span>
          <h3 className="am-intro-title">
            {waiting
              ? tx(lang,
                `${waiting === 1 ? '1 answer' : `${waiting} answers`} will send when you're online`,
                `Ipapadala ang ${waiting} sagot pagbalik ng internet`)
              : tx(lang, 'Your answers are saved', 'Naka-save ang iyong mga sagot')}
          </h3>
          <p className="am-intro-text">
            {waiting
              ? `${tx(lang, `They stay on this ${device} until then.`, `Mananatili ang mga ito sa ${device} na ito hanggang doon.`)} ${pickUp}`
              : pickUp}
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer className="am-foot-split">
        <button type="button" className="am-btn is-soft" onClick={onLeave}>
          {tx(lang, 'Leave for now', 'Aalis muna')}
        </button>
        <button type="button" className="am-btn is-primary" onClick={onStay}>
          {tx(lang, 'Keep going', 'Ituloy')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};
